import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { MessageChat } from '@entities/message';
import { Lightbox } from '@shared/ui';

import { DealNotesFooter } from './DealNotesFooter';
import {
    useCreateDealCommentMutation,
    useDeleteDealCommentMutation,
    useGetDealCommentsPolling,
} from '../../api';
import { EmptyDealNotes } from '../EmptyDealNotes';

import type { TDeal } from '@crm/entities/deal';
import type { ICommentGroup, IMessageAttachment } from '@entities/message';
import type { IPreviewFile } from '@shared/ui/Lightbox/model/types';

interface DealNotesProps {
    dealId: TDeal['id'];
    messagesData: ICommentGroup[];
    stateToken?: string;
}

export const DealNotes = ({ dealId, messagesData, stateToken }: DealNotesProps) => {
    const { t } = useTranslation();
    const createDealCommentMutation = useCreateDealCommentMutation({ dealId });
    const deleteDealCommentMutation = useDeleteDealCommentMutation({ dealId });
    const [lightboxFiles, setLightboxFiles] = useState<IPreviewFile[]>([]);
    const [currentLightboxFileId, setCurrentLightboxFileId] = useState<
        IMessageAttachment['id'] | null
    >(null);

    const openAttachmentLightbox = useCallback(
        (attachments: IMessageAttachment[], currentAttachmentId: IMessageAttachment['id']) => {
            setLightboxFiles(
                attachments.map((attachment) => ({
                    id: attachment.id,
                    fileSrc:
                        attachment.originalUrl || attachment.previewUrl || attachment.previewSvgUrl,
                    mimeType: attachment.mimeType,
                    fileName: attachment.name,
                }))
            );
            setCurrentLightboxFileId(currentAttachmentId);
        },
        []
    );

    const closeAttachmentLightbox = useCallback(() => {
        setLightboxFiles([]);
        setCurrentLightboxFileId(null);
    }, []);
    useGetDealCommentsPolling({ dealId, stateToken });

    return (
        <section className="flex min-h-0 flex-col items-start justify-start gap-5 self-stretch overflow-hidden rounded-xl p-5 outline-1 -outline-offset-1 outline-gray-200">
            <h2 className="text-base leading-5 font-bold text-gray-800">
                {t('crm.dealDetailsContent.notesBlock.title')}
            </h2>
            <MessageChat
                className="smetter-css"
                deciderText={t('crm.dealDetailsContent.notesBlock.newComments')}
                emptyPlaceholder={<EmptyDealNotes />}
                messagesData={messagesData}
                footer={
                    <DealNotesFooter
                        isPending={createDealCommentMutation.isPending}
                        onSubmit={createDealCommentMutation.mutateAsync}
                    />
                }
                onDeleteMessage={deleteDealCommentMutation.mutate}
                onOpenAttachments={openAttachmentLightbox}
            />
            {currentLightboxFileId !== null && (
                <Lightbox
                    files={lightboxFiles}
                    onClose={closeAttachmentLightbox}
                    currentFileId={currentLightboxFileId}
                />
            )}
        </section>
    );
};
